import Phaser from 'phaser';
import { VEHICLE_STATS } from '../config';
import type { VehicleKind, VehicleStats } from '../types';

export class Vehicle {
  readonly sprite: Phaser.Physics.Arcade.Sprite;
  readonly kind: VehicleKind;
  readonly stats: VehicleStats;
  hp: number;
  driver: 'none' | 'player' | 'ai' = 'ai';
  exploded = false;

  constructor(scene: Phaser.Scene, x: number, y: number, kind: VehicleKind) {
    this.kind = kind;
    this.stats = VEHICLE_STATS[kind];
    this.hp = this.stats.hp;
    const tex = this.stats.textures[Math.floor(Math.random() * this.stats.textures.length)];
    const s = scene.physics.add.sprite(x, y, tex);
    s.setOrigin(0.5, 0.5);
    s.setScale(this.stats.scale);
    // Tighten collision body to match car silhouette (sprite has whitespace + mirrors)
    const bw = this.stats.width;
    const bh = this.stats.height;
    s.body!.setSize(bw, bh);
    s.body!.setOffset((s.width - bw) / 2, (s.height - bh) / 2);
    s.setDamping(true);
    s.setDrag(0.85);
    s.setMaxVelocity(this.stats.maxSpeed);
    s.setCollideWorldBounds(true);
    s.setData('vehicle', this);
    this.sprite = s;
  }

  /** Drive with throttle in [-1,1] and steer in [-1,1]. Sprite faces "up" at rotation 0. */
  drive(throttle: number, steer: number, dt: number) {
    if (this.exploded) return;
    const s = this.sprite;
    const v = s.body!.velocity as Phaser.Math.Vector2;
    const speed = v.length();
    // Need motion to turn; full steering above 60 px/s
    s.rotation += steer * this.stats.turn * Math.min(1, speed / 60) * dt;

    if (Math.abs(throttle) > 0.01) {
      const fx = Math.sin(s.rotation);
      const fy = -Math.cos(s.rotation);
      const acc = throttle * this.stats.accel;
      s.setAcceleration(fx * acc, fy * acc);
    } else {
      s.setAcceleration(0, 0);
    }
  }

  damage(n: number) {
    if (this.exploded) return;
    this.hp = Math.max(0, this.hp - n);
    if (this.hp <= 0) this.explode();
  }

  explode() {
    if (this.exploded) return;
    this.exploded = true;
    const s = this.sprite;
    const scene = s.scene;
    const fx = scene.add.circle(s.x, s.y, 6, 0xffaa33, 1);
    scene.tweens.add({
      targets: fx, radius: 60, alpha: 0, duration: 500,
      onComplete: () => fx.destroy()
    });
    s.setTint(0x222222);
    s.setVelocity(0, 0);
    s.setAcceleration(0, 0);
  }

  destroy() { this.sprite.destroy(); }
}
